import { z } from 'zod';
import { amountMinorSchema } from './common';

const dateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');

/** Business dates in the branch time zone, inclusive. */
export const salesReportQuerySchema = z.object({ from: dateSchema, to: dateSchema });

/**
 * Operational sales report (spec §5.10) — based on settled bills; not an accounting P&L.
 * Definitions are returned with the data so the UI can disclose them.
 */
export const salesReportSchema = z.object({
  from: dateSchema,
  to: dateSchema,
  currency: z.string(),
  totals: z.object({
    bills: z.number().int(),
    guests: z.number().int(),
    items_sold: z.number().int(),
    gross_sales_minor: amountMinorSchema,
    service_charge_minor: amountMinorSchema,
    tax_minor: amountMinorSchema,
    rounding_minor: amountMinorSchema,
    total_collected_minor: amountMinorSchema,
    average_check_minor: amountMinorSchema,
    cancelled_items: z.number().int(),
    /** Refunds given in the range (by refund date, ADR-0051). */
    refunds_minor: amountMinorSchema,
    /** Payments received in the range (by payment date) − refunds: the money actually kept. */
    net_received_minor: amountMinorSchema,
  }),
  by_day: z.array(
    z.object({ date: dateSchema, bills: z.number().int(), gross_sales_minor: amountMinorSchema }),
  ),
  by_hour: z.array(
    z.object({
      hour: z.number().int(),
      bills: z.number().int(),
      gross_sales_minor: amountMinorSchema,
    }),
  ),
  by_payment_method: z.array(z.object({ method: z.string(), amount_minor: amountMinorSchema })),
  /**
   * Cash flow by the day money moved (branch time zone), whatever day the bill belongs to: a
   * bill paid later counts here on its payment day, in `by_payment_method` on its bill day.
   */
  received_by_method: z.array(
    z.object({
      method: z.string(),
      received_minor: amountMinorSchema,
      refunded_minor: amountMinorSchema,
    }),
  ),
  /** Dine-in / takeaway / each delivery platform. */
  by_channel: z.array(
    z.object({
      order_type: z.string(),
      channel: z.string(),
      bills: z.number().int(),
      total_collected_minor: amountMinorSchema,
    }),
  ),
  top_items: z.array(
    z.object({
      name: z.string(),
      quantity: z.number().int(),
      gross_sales_minor: amountMinorSchema,
    }),
  ),
  by_category: z.array(
    z.object({
      name: z.string(),
      quantity: z.number().int(),
      gross_sales_minor: amountMinorSchema,
    }),
  ),
  definitions: z.record(z.string(), z.string()),
});
export type SalesReport = z.infer<typeof salesReportSchema>;
